import React from 'react';
import { useMemoizedValue } from '@sail/react';
import { useController } from '../../lib/react-hook-form';
import { useFormContext } from './useFormContext';
import { getDummyControl } from './dummyControl';
import { useWrappedFormValidation } from './useWrappedFormValidation';
export const useFormRegister = ({ props: originalProps, valueProp = 'value', changeProp = 'onChange', valueAdapter, changeAdapter })=>{
    var _fieldState_error;
    const form = useFormContext();
    const props = useMemoizedValue(originalProps);
    const { wrappedRules, extractError } = useWrappedFormValidation(props.rules);
    const { field, fieldState, formState } = useController({
        control: (form === null || form === void 0 ? void 0 : form.control) || getDummyControl(),
        rules: wrappedRules,
        name: props.name || '__dummy__',
        defaultValue: props.defaultValue,
        disabled: props.disabled
    });
    const adaptedValue = React.useMemo(()=>{
        const base = valueAdapter ? valueAdapter(field.value, props.value) : field.value;
        // Coerce undefined -> null for the bound value prop so fields mount controlled
        // even when defaultValues arrive async. This prevents "controlled vs uncontrolled"
        // flips and ensures programmatic updates reflect correctly while editing.
        return base !== null && base !== void 0 ? base : null;
    }, [
        valueAdapter,
        field.value,
        props.value
    ]);
    const adaptedOnChange = React.useCallback((event)=>{
        const transformedValue = changeAdapter ? changeAdapter(event, field.value, props.value) : event;
        field.onChange(transformedValue);
        const propsOnChangeCallback = props[changeProp];
        if (typeof propsOnChangeCallback === 'function') {
            propsOnChangeCallback(event);
        }
    }, [
        changeAdapter,
        changeProp,
        field,
        props
    ]);
    const componentProps = React.useMemo(()=>{
        var _fieldState_error;
        const { value: _, onChange: __, ...restField } = field;
        // 1. Remove rules object, we don't want it in the component.
        // 2. Remove value, but only if valueProp is 'value'. Otherwise, we want
        //    both props (value and [valueProp]) to be available to the component.
        //    Certain components (e.g. Radio) need both props to be available.
        const restProps = valueProp === 'value' ? (({ value, rules, ...rest })=>rest)(props) : (({ rules, ...rest })=>rest)(props);
        const result = {
            ...restProps,
            ...restField,
            [valueProp]: adaptedValue,
            [changeProp]: adaptedOnChange,
            disabled: restProps.disabled || formState.disabled,
            error: extractError((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message)
        };
        return result;
    }, [
        props,
        field,
        formState.disabled,
        (_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message,
        valueProp,
        changeProp,
        adaptedValue,
        adaptedOnChange,
        extractError
    ]);
    // When not in a Sail Form context or name not provided, act as noop.
    // Return props as-is.
    if (!(form === null || form === void 0 ? void 0 : form.control) || !props.name) {
        return {
            isInSailForm: false,
            componentProps: props
        };
    }
    return {
        isInSailForm: true,
        componentProps
    };
};

//# sourceMappingURL=useFormRegister.js.map